/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxiosForClient } from '@/lib/axios';
import { type QueryClient, type QueryFunctionContext, useQuery } from '@tanstack/react-query';
import type { CitrusBannerResponse } from '../interface';
import { type PageContextType } from '@/next-types';
import { getCitrusCategoryData } from '../utils/apiUtils.server';
import { useFeatureFlag } from '@/features/globalConfig';
import { getDecodedCookiesSync } from '@/utils/cookie';
import { getCitrusCatalogId } from '../utils/getCitrusCatalogId';
import { getCitrusContentStandardId } from '../utils/getCitrusContentStandardId';
import environmentConfig from '@/config/environment';
import { getCitrusProxyConfiguration } from '../lib/getCitrusProxyConfiguration';

const URL = `/api/citrus/category`;
const PROXY_URL = '/v1/ads/bannerx';

const getCitrusCategorypageDataWithProxy = async ({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof getCitrusCategorypageKey>>) => {
  const [, , category] = queryKey;
  const cookies = getDecodedCookiesSync(['eCookieId', 'rewardsId', 'forceCitrusProd']);
  const rewardsId = cookies.get('rewardsId');
  const eCookieId = cookies.get('eCookieId');
  const forceCitrusProd = !!cookies.get('forceCitrusProd');
  const productFilters = [[`category:${category}`]];
  const { baseURL, axiosConfig } = getCitrusProxyConfiguration();

  const response = await getAxiosForClient().post<CitrusBannerResponse>(
    `${baseURL}${PROXY_URL}`,
    {
      customerId: rewardsId || null,
      sessionId: rewardsId || eCookieId,
      catalogId: getCitrusCatalogId(forceCitrusProd),
      contentStandardId: getCitrusContentStandardId(forceCitrusProd),
      placement: 'category-banner-x',
      options: {
        filterMode: 'AndOr',
      },
      productFilters,
      bannerSlotIds: [
        {
          slotId: 'midpage-banner',
          maxNumberOfAds: 1,
        },
      ],
    },
    axiosConfig,
  );
  return selector(response.data);
};

const getCitrusCategorypageData = async ({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof getCitrusCategorypageKey>>) => {
  const [, , category] = queryKey;

  const params = {
    category,
  };

  const response = await getAxiosForClient().get<CitrusBannerResponse>(URL, { params });
  return selector(response.data);
};

const selector = (data: CitrusBannerResponse): CitrusBannerResponse => data;

const getCitrusCategorypageKey = (category: string) =>
  ['citrus', 'categorypage', category] as const;

export const useCitrusCategorypageData = (
  category: string,
  select?: (headerData: CitrusBannerResponse) => CitrusBannerResponse,
) => {
  const citrusEnabled = useFeatureFlag('IS_CITRUS_ENABLED') === 'true';
  const citrusCategoryEnabled = useFeatureFlag('IS_CITRUS_CATEGORY_BANNERS_ENABLED') === 'true';
  const CITRUS_PROXY_DISABLED = environmentConfig.CITRUS_PROXY_DISABLED;
  return useQuery({
    enabled: Boolean(category) && citrusEnabled && citrusCategoryEnabled,
    queryKey: getCitrusCategorypageKey(category),
    queryFn: !CITRUS_PROXY_DISABLED
      ? getCitrusCategorypageDataWithProxy
      : getCitrusCategorypageData,
    select,
  });
};

export const prefetchCitrusCategoryData = (
  queryClient: QueryClient,
  context: PageContextType,
  category: string,
  eCookieId: string,
) => {
  return queryClient.prefetchQuery({
    queryKey: getCitrusCategorypageKey(category),
    queryFn: () =>
      getCitrusCategoryData({ req: context.req, res: context.res, query: { category, eCookieId } }),
  });
};
